/**
 * AdminToolsPage – Verwaltung der EXTERNEN Tool-Portal-Benutzer
 * (tools.sijelly.com). Nur für interne Admins (rolle=admin).
 *
 * Backend: /tools-portal/admin/* (require_admin, X-MA-Token).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";
import { useAuth } from "../context/AuthContext";
import "./AdminToolsPage.css";

const api = (path, opts = {}) =>
  fetch(`${API_BASE}/tools-portal/admin${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      "X-MA-Token": localStorage.getItem("maToken") || "",
      ...(opts.headers || {}),
    },
  });

const LEER = { login: "", name: "", firma: "", passwort: "", aktiv: true, tools: {} };

export default function AdminToolsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [katalog, setKatalog] = useState([]);
  const [benutzer, setBenutzer] = useState([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState("");
  const [modal, setModal] = useState(null); // {modus:"neu"|"edit", id?, form}
  const [saving, setSaving] = useState(false);

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler("");
    try {
      const [kr, br] = await Promise.all([api("/katalog"), api("/benutzer")]);
      if (!kr.ok || !br.ok) throw new Error("Laden fehlgeschlagen (Admin-Rechte?)");
      setKatalog((await kr.json()).katalog || []);
      setBenutzer((await br.json()).benutzer || []);
    } catch (e) {
      setFehler(String(e.message || e));
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { lade(); }, [lade]);

  const openNeu = () => setModal({ modus: "neu", form: { ...LEER, tools: {} } });
  const openEdit = (b) =>
    setModal({
      modus: "edit",
      id: b.id,
      form: {
        login: b.login, name: b.name || "", firma: b.firma || "",
        passwort: "", aktiv: !!b.aktiv,
        tools: Object.fromEntries(katalog.map((k) => [k.tool_key, b.tools.includes(k.tool_key)])),
      },
    });

  const setF = (key, val) => setModal((m) => ({ ...m, form: { ...m.form, [key]: val } }));
  const toggleTool = (key) =>
    setModal((m) => ({
      ...m,
      form: { ...m.form, tools: { ...m.form.tools, [key]: !m.form.tools[key] } },
    }));

  const speichern = async () => {
    const f = modal.form;
    if (modal.modus === "neu" && (!f.login.trim() || !f.passwort)) {
      alert("Login und Passwort sind Pflicht"); return;
    }
    setSaving(true);
    try {
      if (modal.modus === "neu") {
        const r = await api("/benutzer", {
          method: "POST",
          body: JSON.stringify({
            login: f.login, name: f.name, firma: f.firma,
            passwort: f.passwort, aktiv: f.aktiv, tools: f.tools,
          }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || "Anlegen fehlgeschlagen");
      } else {
        const r1 = await api(`/benutzer/${modal.id}`, {
          method: "PUT",
          body: JSON.stringify({ name: f.name, firma: f.firma, aktiv: f.aktiv }),
        });
        if (!r1.ok) throw new Error("Speichern fehlgeschlagen");
        const r2 = await api(`/benutzer/${modal.id}/tools`, {
          method: "PUT", body: JSON.stringify(f.tools),
        });
        if (!r2.ok) throw new Error("Tool-Freigaben speichern fehlgeschlagen");
        if (f.passwort) {
          const r3 = await api(`/benutzer/${modal.id}/passwort`, {
            method: "PUT", body: JSON.stringify({ passwort: f.passwort }),
          });
          if (!r3.ok) throw new Error("Passwort setzen fehlgeschlagen");
        }
      }
      setModal(null);
      lade();
    } catch (e) {
      alert(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const loeschen = async (b) => {
    if (!window.confirm(`Benutzer „${b.login}" (${b.firma || "ohne Firma"}) wirklich löschen?`)) return;
    const r = await api(`/benutzer/${b.id}`, { method: "DELETE" });
    if (!r.ok) alert("Löschen fehlgeschlagen");
    lade();
  };

  const toggleAktiv = async (b) => {
    const r = await api(`/benutzer/${b.id}`, {
      method: "PUT", body: JSON.stringify({ aktiv: !b.aktiv }),
    });
    if (!r.ok) alert("Ändern fehlgeschlagen");
    lade();
  };

  const toolName = (key) => katalog.find((k) => k.tool_key === key)?.name || key;

  if (user && user.rolle !== "admin") {
    return <div className="at-page"><div className="at-fehler">Nur für Admins.</div></div>;
  }

  return (
    <div className="at-page">
      <div className="at-header">
        <button className="at-back" onClick={() => navigate("/")}>← Home</button>
        <h1>🧰 Tool-Portal Benutzer</h1>
        <div className="at-header-right">
          <span className="at-hint">tools.sijelly.com</span>
          <button className="at-btn primary" onClick={openNeu}>+ Neuer Benutzer</button>
        </div>
      </div>

      {fehler && <div className="at-fehler">{fehler}</div>}
      {laden ? (
        <div className="at-laden">Lädt…</div>
      ) : (
        <div className="at-tabelle-wrap">
          <table className="at-tabelle">
            <thead>
              <tr>
                <th>Firma</th><th>Login</th><th>Name</th><th>Tools</th>
                <th>Status</th><th>Letzter Login</th><th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {benutzer.map((b) => (
                <tr key={b.id} className={b.aktiv ? "" : "inaktiv"}>
                  <td>{b.firma || "—"}</td>
                  <td><code>{b.login}</code></td>
                  <td>{b.name || "—"}</td>
                  <td>
                    {b.tools.length
                      ? b.tools.map((t) => <span key={t} className="at-badge">{toolName(t)}</span>)
                      : <span className="at-badge leer">keine</span>}
                  </td>
                  <td>
                    <span className={`at-status ${b.aktiv ? "an" : "aus"}`}>
                      {b.aktiv ? "aktiv" : "deaktiviert"}
                    </span>
                  </td>
                  <td>{b.letzter_login ? b.letzter_login.slice(0, 16).replace("T", " ") : "—"}</td>
                  <td className="at-aktionen">
                    <button className="at-btn small" onClick={() => openEdit(b)}>Bearbeiten</button>
                    <button className="at-btn small" onClick={() => toggleAktiv(b)}>
                      {b.aktiv ? "Deaktivieren" : "Aktivieren"}
                    </button>
                    <button className="at-btn small danger" onClick={() => loeschen(b)}>Löschen</button>
                  </td>
                </tr>
              ))}
              {!benutzer.length && (
                <tr><td colSpan={7} className="at-leer">Noch keine Tool-Benutzer angelegt.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="at-modal-bg">
          <div className="at-modal">
            <h2>{modal.modus === "neu" ? "Neuer Tool-Benutzer" : `Benutzer „${modal.form.login}"`}</h2>
            <label className="at-label">Login (Benutzername) *
              <input className="at-input" value={modal.form.login} disabled={modal.modus === "edit"}
                autoCapitalize="none" onChange={(e) => setF("login", e.target.value)} />
            </label>
            <label className="at-label">Firma
              <input className="at-input" value={modal.form.firma} onChange={(e) => setF("firma", e.target.value)} />
            </label>
            <label className="at-label">Name (Ansprechpartner)
              <input className="at-input" value={modal.form.name} onChange={(e) => setF("name", e.target.value)} />
            </label>
            <label className="at-label">
              {modal.modus === "neu" ? "Passwort *" : "Neues Passwort (leer = unverändert)"}
              <input className="at-input" type="text" value={modal.form.passwort}
                onChange={(e) => setF("passwort", e.target.value)} />
            </label>
            <div className="at-tools-box">
              <div className="at-tools-titel">Freigeschaltete Tools</div>
              {katalog.map((k) => (
                <label key={k.tool_key} className="at-tool-check">
                  <input type="checkbox" checked={!!modal.form.tools[k.tool_key]}
                    onChange={() => toggleTool(k.tool_key)} />
                  <span>{k.icon} {k.name}</span>
                  <span className="at-tool-beschr">{k.beschreibung}</span>
                </label>
              ))}
            </div>
            <label className="at-tool-check">
              <input type="checkbox" checked={!!modal.form.aktiv} onChange={(e) => setF("aktiv", e.target.checked)} />
              <span>Zugang aktiv</span>
            </label>
            <div className="at-modal-buttons">
              <button className="at-btn" onClick={() => setModal(null)} disabled={saving}>Abbrechen</button>
              <button className="at-btn primary" onClick={speichern} disabled={saving}>
                {saving ? "Speichert…" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
